import { useNavigate, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import FamousShipmentModal from './components/FamousShipmentModal';
import useFamousShipment from 'src/api/temp/famous/useFamousShipment';
import { Card, CardContent } from 'src/components/ui/card';
import { Spinner } from 'src/components/ui/spinner';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';

export function Route() {
  const { shipmentId } = useParams({
    from: '/authenticated/famous-edi-shipments/$shipmentId',
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: shipment,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useFamousShipment(shipmentId);
  const hasRefetched = useRef(false);

  useEffect(() => {
    if (
      shipment &&
      shipment.edi856Status === 'pending' &&
      !hasRefetched.current
    ) {
      hasRefetched.current = true;
      refetch();
    }
  }, [shipment, refetch]);

  const handleClose = (_shouldRefetch?: boolean) => {
    queryClient.invalidateQueries({ queryKey: ['famous/shipments'] });
    navigate({
      to: '..',
      viewTransition: true,
    });
  };

  // Show loading state while fetching
  if (isLoading) {
    return (
      <Card className="p-2 m-2">
        <CardContent>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            p={3}
          >
            <Spinner size="sm" className="mr-2" />
            <MDTypography variant="body2">Loading shipment...</MDTypography>
          </MDBox>
        </CardContent>
      </Card>
    );
  }

  // Show error state
  if (error) {
    return (
      <Card className="p-2 m-2">
        <CardContent>
          <ErrorMessage error={error} />
        </CardContent>
      </Card>
    );
  }

  // Show modal only when data is loaded
  if (!shipment) {
    return null;
  }

  return (
    <FamousShipmentModal
      famousShipment={shipment}
      onClose={handleClose}
      isFetching={isFetching}
    />
  );
}
